"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin, Phone, Clock, Navigation } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const openingHours = [{
  day: "Maandag",
  hours: "Gesloten",
  jsDay: 1
}, {
  day: "Dinsdag",
  hours: "09:30 - 17:30",
  jsDay: 2
}, {
  day: "Woensdag",
  hours: "09:30 - 17:30",
  jsDay: 3
}, {
  day: "Donderdag",
  hours: "09:30 - 17:30",
  jsDay: 4
}, {
  day: "Vrijdag",
  hours: "09:30 - 20:00",
  jsDay: 5
}, {
  day: "Zaterdag",
  hours: "09:30 - 17:00",
  jsDay: 6
}, {
  day: "Zondag",
  hours: "Gesloten",
  jsDay: 0
}];
export default function VindOnzeShop() {
  const [currentDay, setCurrentDay] = useState<number | null>(null);
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  useEffect(() => {
    setCurrentDay(new Date().getDay());
  }, []);
  return <section data-section-id="80668" id="kom-langs-in-de-shop" className="dark bg-background text-foreground py-16 md:py-24 relative overflow-hidden" ref={sectionRef}>
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="lg:col-span-7 relative min-h-[400px] lg:min-h-[600px] rounded-sm overflow-hidden border border-border shadow-2xl group">
            {}
            <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577285/img/dark_grayscale_map_middelharnis.png)] bg-cover bg-center transition-transform duration-1000 group-hover:scale-105" />
            <div className="absolute inset-0 bg-background/60 mix-blend-multiply" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />

            {}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
              <div className="relative">
                <div className="absolute inset-0 bg-primary/40 rounded-full animate-ping" />
                <div className="bg-card p-3 rounded-sm border border-primary shadow-lg relative z-10">
                  <MapPin className="w-8 h-8 text-primary" />
                </div>
              </div>
              <div className="mt-4 bg-card/95 backdrop-blur-md px-6 py-3 rounded-sm border border-primary/30 shadow-2xl text-center">
                <p className="font-sans font-bold uppercase tracking-widest text-primary text-sm">
                  H&E Kappers
                </p>
                <p className="text-xs text-foreground/80 mt-1">Zandpad 2, Middelharnis</p>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 flex flex-col justify-center">
            <Card className="bg-card border-border shadow-2xl h-full flex flex-col rounded-sm">
              <CardHeader className="pb-6 border-b border-border">
                <CardTitle className="font-sans font-bold uppercase tracking-widest text-3xl md:text-4xl text-foreground">
                  Bezoek Onze Shop
                </CardTitle>
                <CardDescription className="text-muted-foreground text-base mt-2 font-sans">
                  Klaar voor een superieure fade of klassieke snit? Kom langs in onze premium 
                  barbershop in het centrum van Middelharnis.
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-8 flex-grow flex flex-col gap-8">
                {}
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 p-3 rounded-sm shrink-0 border border-primary/20">
                      <MapPin className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold uppercase tracking-wider text-xs text-primary mb-1">
                        Locatie
                      </h4>
                      <p className="text-foreground font-sans">
                        Zandpad 2<br />
                        3241 GX Middelharnis<br />
                        Nederland
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 p-3 rounded-sm shrink-0 border border-primary/20">
                      <Phone className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold uppercase tracking-wider text-xs text-primary mb-1">
                        Contact
                      </h4>
                      <p className="text-foreground font-sans">+31 6 39394199</p>
                      <p className="text-foreground font-sans mt-2">
                        <span className="text-muted-foreground text-xs block mb-0.5">U kunt ons ook bereiken via e-mail:</span>
                        info@hekappers.nl
                      </p>
                    </div>
                  </div>
                </div>

                {}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-5 h-5 text-primary" />
                    <h4 className="font-bold uppercase tracking-wider text-sm text-foreground">
                      Openingstijden
                    </h4>
                  </div>
                  <div className="bg-background/40 rounded-sm border border-border overflow-hidden">
                    <Table>
                      <TableBody>
                        {openingHours.map((item, i) => {
                        const isToday = currentDay === item.jsDay;
                        return <TableRow key={item.day} data-index={i} className={`border-border/40 hover:bg-transparent ${isToday ? "bg-primary/5" : ""}`}>
                              <TableCell className={`py-3 pl-4 font-sans ${isToday ? "text-primary font-bold" : "text-muted-foreground"}`}>
                                {item.day}
                                {isToday && <span className="ml-2 text-[9px] uppercase font-bold tracking-widest bg-primary text-primary-foreground px-2 py-0.5 rounded-sm">
                                    Vandaag
                                  </span>}
                              </TableCell>
                              <TableCell className={`py-3 pr-4 text-right font-sans ${isToday ? "text-primary font-bold" : "text-foreground"}`}>
                                {item.hours}
                              </TableCell>
                            </TableRow>;
                      })}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {}
                <div className="mt-auto pt-4">
                  <Button asChild size="lg" className="w-full group bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-widest rounded-sm">
                    <Link to="https://maps.google.com/?q=zandpad+2,+3241+GX+Middelharnis" newTab>
                      <Navigation className="w-4 h-4 mr-2 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" />
                      Plan Je Route
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}